import { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";
import { loadFaceModels } from "../lib/faceModels";
import {
  Camera,
  KeyRound,
  Clock,
  CheckCircle2,
  XCircle,
  LogIn,
  Coffee,
  LogOut,
  ArrowLeft,
  Loader2,
  ScanFace,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import api from "../lib/api";

const EVENT_TYPES = [
  { value: "check_in", label: "Entrada", icon: LogIn },
  { value: "meal_out", label: "Sal. Comida", icon: Coffee },
  { value: "meal_in", label: "Reg. Comida", icon: Coffee },
  { value: "check_out", label: "Salida", icon: LogOut },
];

const QUEUE_KEY = "vetcomer_kiosk_offline_queue";
const FACES_CACHE_KEY = "vetcomer_kiosk_faces_cache";
const RECENT_CACHE_KEY = "vetcomer_kiosk_recent_cache";
const DEVICE_ID_KEY = "vetcomer_kiosk_device";
const DEVICE_TOKEN_KEY = "vetcomer_kiosk_token";
const BRANCH_KEY = "vetcomer_kiosk_branch";
const DAY_STATE_KEY = "vetcomer_kiosk_day_state";  // mapa { employee_id -> [eventos] } por día
const HEARTBEAT_INTERVAL_MS = 60_000;       // 60s
const IDLE_RELOAD_AFTER_MS = 5 * 60 * 1000; // 5 min
const KIOSK_IDLE_MS = 60_000;               // 60s sin rostro/marca → pantalla de reposo
const FACES_SIGNAL_INTERVAL_MS = 15_000;    // cada 15s revisa si cambiaron los rostros enrolados

const queueGet = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
};
const queueSet = (q) => { try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* quota */ } };

// ───────────────────────────────────────────────────────────────────────
// Day-state tracker — replica la lógica de /api/kiosk/employee-status en
// el iPad para que el kiosko funcione 100% offline sin perder validación.
// Estructura: { "2026-06-01": { "<employee_id>": ["check_in","meal_out",...] } }
// ───────────────────────────────────────────────────────────────────────
const todayKey = () => new Date().toISOString().slice(0, 10);

const dayStateGet = () => {
  try { return JSON.parse(localStorage.getItem(DAY_STATE_KEY) || "{}"); } catch { return {}; }
};
const dayStateSet = (s) => { try { localStorage.setItem(DAY_STATE_KEY, JSON.stringify(s)); } catch { /* quota */ } };

/** Carga los modelos de face-api con reintentos locales y fallback a CDN. */
async function loadFaceModelsWithRetry() {
  await loadFaceModels();
}


/** Borra entradas de días anteriores (mantiene solo hoy). */
const dayStatePruneOld = () => {
  const all = dayStateGet();
  const today = todayKey();
  const trimmed = today in all ? { [today]: all[today] } : {};
  dayStateSet(trimmed);
};

/** Registra que un empleado marcó un tipo de evento hoy. */
const dayStateRecord = (employeeId, eventType) => {
  if (!employeeId || !eventType) return;
  const all = dayStateGet();
  const today = todayKey();
  if (!all[today]) all[today] = {};
  if (!all[today][employeeId]) all[today][employeeId] = [];
  all[today][employeeId].push(eventType);
  dayStateSet(all);
};

/** Lista de eventos que un empleado ya marcó hoy. */
const dayStateGetEvents = (employeeId) => {
  const all = dayStateGet();
  return all[todayKey()]?.[employeeId] || [];
};

/**
 * Calcula `{ allowed_events, suggested_event, has_check_out, has_check_in, is_split_shift_active }`
 * basándose en los eventos del día (replica la lógica del backend).
 * No considera `reentry_allowed` porque es un permiso que solo existe en el servidor.
 */
const computeStatusFromEvents = (events) => {
  const ev = events || [];
  const last = ev[ev.length - 1] || null;
  const has_check_in = ev.includes("check_in");
  // Para detectar split-shift / segundo check_out, mirar desde el último check_out hacia atrás
  let has_check_out = false;
  let is_split_shift_active = false;
  for (let i = ev.length - 1; i >= 0; i--) {
    if (ev[i] === "check_out") { has_check_out = true; break; }
    if (ev[i] === "check_in") { is_split_shift_active = true; break; }
  }
  // Determinar eventos permitidos
  let allowed = [];
  let suggested = null;
  if (ev.length === 0) {
    allowed = ["check_in"];
    suggested = "check_in";
  } else if (last === "check_in" || last === "meal_in") {
    allowed = ["meal_out", "check_out"];
    suggested = "meal_out";
  } else if (last === "meal_out") {
    allowed = ["meal_in"];
    suggested = "meal_in";
  } else if (last === "check_out") {
    // Día finalizado localmente. OFFLINE no podemos saber si RRHH otorgó
    // reingreso, así que sugerimos "Entrada" como acción por defecto (los
    // botones se habilitan igual y el servidor valida al sincronizar).
    allowed = ["check_in"];
    suggested = "check_in";
  }
  return {
    allowed_events: allowed,
    suggested_event: suggested,
    has_check_in,
    has_check_out,
    is_split_shift_active,
    reentry_allowed: false,    // solo el servidor lo sabe
    offline_status: true,      // marca que esto vino del cliente
  };
};

const isNetworkError = (err) => {
  if (!err) return false;
  if (err.message === "__offline__") return true;
  // Axios: no response, or code ERR_NETWORK / timeout
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  return !err.response;
};

export default function Kiosk() {
  // Setup state
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [phase, setPhase] = useState("setup"); // setup | loading | kiosk
  const [activationCode, setActivationCode] = useState("");
  const [activating, setActivating] = useState(false);

  // Kiosk state
  const [clock, setClock] = useState(new Date());
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [enrolledFaces, setEnrolledFaces] = useState([]);
  const [cameraReady, setCameraReady] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [identifiedEmployee, setIdentifiedEmployee] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState("check_in");
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message, employee }
  const [recentMarks, setRecentMarks] = useState([]);
  const [showPin, setShowPin] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [faceBox, setFaceBox] = useState(null); // { x, y, w, h } relative percentages
  const [employeeStatus, setEmployeeStatus] = useState(null); // { allowed_events, suggested_event, ... }
  const [faceOutOfFrame, setFaceOutOfFrame] = useState(false); // face detected but outside guide
  const [showPinFallback, setShowPinFallback] = useState(false); // enable PIN after repeated match failures
  const [pendingCount, setPendingCount] = useState(() => queueGet().length);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [kioskActive, setKioskActive] = useState(false); // false = pantalla de reposo (cámara apagada)

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const identifiedRef = useRef(null);
  const feedbackRef = useRef(null);
  const enrolledRef = useRef([]);
  const failedMatchCountRef = useRef(0); // consecutive in-frame unmatched attempts
  const flushingRef = useRef(false);
  const lastActivityRef = useRef(Date.now()); // última vez que hubo rostro/marca
  const facesVersionRef = useRef(null); // señal {count, latest} de rostros enrolados

  // Keep refs in sync with state
  useEffect(() => { identifiedRef.current = identifiedEmployee; }, [identifiedEmployee]);
  useEffect(() => { feedbackRef.current = feedback; }, [feedback]);
  useEffect(() => { enrolledRef.current = enrolledFaces; }, [enrolledFaces]);

  // Refresca los rostros enrolados en segundo plano (sin interrumpir la UI).
  const refreshEnrolledFaces = useCallback(async () => {
    if (!selectedBranch) return;
    try {
      const { data } = await api.get("/kiosk/enrolled-faces", {
        params: { branch_id: selectedBranch.id },
      });
      setEnrolledFaces(data);
      try {
        localStorage.setItem(`${FACES_CACHE_KEY}_${selectedBranch.id}`, JSON.stringify(data));
      } catch { /* quota */ }
    } catch { /* mantener rostros actuales si falla */ }
  }, [selectedBranch]);

  // Despierta el kiosko desde el reposo: enciende cámara + refresca rostros.
  const wakeKiosk = useCallback(() => {
    lastActivityRef.current = Date.now();
    setKioskActive(true);
    refreshEnrolledFaces();
  }, [refreshEnrolledFaces]);

  // When an employee is identified, fetch their today-state and set smart button defaults.
  // Si estamos offline o el servidor no responde, usamos el tracker LOCAL del día.
  useEffect(() => {
    let cancelled = false;
    if (!identifiedEmployee?.employee_id) {
      setEmployeeStatus(null);
      return;
    }
    const prevId = identifiedEmployee.employee_id;

    const applyLocal = () => {
      const localEvents = dayStateGetEvents(prevId);
      const localStatus = computeStatusFromEvents(localEvents);
      setEmployeeStatus(localStatus);
      if (localStatus.suggested_event) setSelectedEvent(localStatus.suggested_event);
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      applyLocal();
      return;
    }

    api
      .get("/kiosk/employee-status", { params: { employee_id: prevId } })
      .then(({ data }) => {
        if (cancelled) return;
        setEmployeeStatus(data);
        if (data.suggested_event) setSelectedEvent(data.suggested_event);
      })
      .catch(() => {
        // Backend caído / sin red → fallback al tracker local
        if (!cancelled) applyLocal();
      });
    return () => { cancelled = true; };
  }, [identifiedEmployee?.employee_id]);


  // Clock tick
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ──────────────────────────────────────────────────────────────────
  // OFFLINE QUEUE — flush pending marks when back online
  // ──────────────────────────────────────────────────────────────────
  const flushQueue = useCallback(async () => {
    if (flushingRef.current) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const q = queueGet();
    if (q.length === 0) {
      setPendingCount(0);
      return;
    }
    flushingRef.current = true;
    let cursor = 0;
    try {
      while (cursor < q.length) {
        const item = q[cursor];
        try {
          await api.post("/kiosk/mark", item);
          cursor += 1;
        } catch (err) {
          if (isNetworkError(err)) {
            // Still offline — stop, keep everything from cursor on
            break;
          }
          // Backend rejected (4xx) — drop this item and continue
          console.warn("Dropping queued mark (rejected):", err?.response?.data);
          cursor += 1;
        }
      }
    } finally {
      const remaining = q.slice(cursor);
      queueSet(remaining);
      setPendingCount(remaining.length);
      flushingRef.current = false;
      if (cursor > 0 && selectedBranch) {
        // Refresh recent marks after successful flush
        try {
          const { data } = await api.get("/kiosk/recent-marks", {
            params: { branch_id: selectedBranch.id },
          });
          setRecentMarks(data);
          try {
            localStorage.setItem(`${RECENT_CACHE_KEY}_${selectedBranch.id}`, JSON.stringify(data));
          } catch { /* quota */ }
        } catch { /* ignore */ }
      }
    }
  }, [selectedBranch]);

  // Online / offline listeners + periodic flush
  useEffect(() => {
    const onOnline = () => { setIsOnline(true); flushQueue(); };
    const onOffline = () => { setIsOnline(false); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    flushQueue();
    const t = setInterval(flushQueue, 30000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(t);
    };
  }, [flushQueue]);

  // Read ?code=VET-XXXX-XX from URL (QR deep link) → prefill and auto-activate
  useEffect(() => {
    if (phase !== "setup") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code && !activationCode) {
      const clean = code.trim().toUpperCase();
      setActivationCode(clean);
      // Strip the param from the URL so refresh won't retry forever
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      window.history.replaceState({}, "", url.toString());
      // Trigger activation on next tick so state flushes
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="kiosk-activate-button"]');
        if (btn) btn.click();
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Validate session on mount (token-based, no code needed)
  useEffect(() => {
    // Limpia day-state de días viejos al arrancar (mantiene SOLO hoy)
    dayStatePruneOld();

    let cancelled = false;
    const restore = async () => {
      const deviceId = localStorage.getItem(DEVICE_ID_KEY);
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      const savedBranch = localStorage.getItem(BRANCH_KEY);

      // Sin device_id+token guardados → primer arranque, debe activar con código
      if (!deviceId || !deviceToken) return;

      try {
        const { data } = await api.post("/kiosk/session/validate", {
          device_id: JSON.parse(deviceId),
          device_token: JSON.parse(deviceToken),
        });
        if (cancelled) return;
        const branch = {
          id: data.branch_id,
          name: data.branch_name,
          code: data.branch_code,
          timezone_offset_minutes: data.timezone_offset_minutes,
        };
        localStorage.setItem(BRANCH_KEY, JSON.stringify(branch));
        setSelectedBranch(branch);
        setDeviceInfo({
          device_id: data.device_id,
          device_name: data.device_name,
          branch_id: data.branch_id,
          branch_name: data.branch_name,
          branch_code: data.branch_code,
        });
        setPhase("loading");
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          // Sesión revocada → limpiar y volver a setup
          localStorage.removeItem(DEVICE_ID_KEY);
          localStorage.removeItem(DEVICE_TOKEN_KEY);
          localStorage.removeItem(BRANCH_KEY);
          toast.error(err.response.data?.detail || "Sesión revocada. Solicite un código nuevo al administrador.");
          setPhase("setup");
        } else if (savedBranch) {
          // Sin conexión → modo offline con caché de la sucursal
          try {
            const branch = JSON.parse(savedBranch);
            setSelectedBranch(branch);
            setPhase("loading");
          } catch { /* ignore */ }
        }
      }
    };
    restore();
    return () => { cancelled = true; };
  }, []);

  // Load models + faces when branch is selected (with offline cache fallback)
  useEffect(() => {
    if (phase !== "loading" || !selectedBranch) return;
    const init = async () => {
      // 1) Modelos biométricos con reintentos (tolera fallos transitorios).
      try {
        await loadFaceModelsWithRetry();
        setModelsLoaded(true);
      } catch (err) {
        console.error("Model load error:", err);
        toast.error("Error al cargar modelos biometricos. Revise su conexion e intente nuevamente.");
        setPhase("kiosk");
        return;
      }

      // 2) Rostros enrolados + marcas recientes (con fallback a cache offline).
      const cacheKeyFaces = `${FACES_CACHE_KEY}_${selectedBranch.id}`;
      const cacheKeyMarks = `${RECENT_CACHE_KEY}_${selectedBranch.id}`;
      try {
        const { data } = await api.get("/kiosk/enrolled-faces", {
          params: { branch_id: selectedBranch.id },
        });
        setEnrolledFaces(data);
        try { localStorage.setItem(cacheKeyFaces, JSON.stringify(data)); } catch { /* quota */ }
      } catch (err) {
        // Offline fallback: rehydrate from cache so face-id still works
        const cached = localStorage.getItem(cacheKeyFaces);
        if (cached) {
          try {
            setEnrolledFaces(JSON.parse(cached));
            toast.warning("Sin conexion: usando rostros en cache");
          } catch { /* ignore */ }
        } else {
          console.error("Enrolled faces load error:", err);
          toast.warning("No se pudieron cargar los rostros enrolados. Reintente cuando haya conexion.");
        }
      }

      try {
        const marksRes = await api.get("/kiosk/recent-marks", {
          params: { branch_id: selectedBranch.id, limit: 50 },
        });
        setRecentMarks(marksRes.data);
        try { localStorage.setItem(cacheKeyMarks, JSON.stringify(marksRes.data)); } catch { /* quota */ }
      } catch {
        const cached = localStorage.getItem(cacheKeyMarks);
        if (cached) {
          try { setRecentMarks(JSON.parse(cached)); } catch { /* ignore */ }
        }
      }

      setPhase("kiosk");
    };
    init();
  }, [phase, selectedBranch]);

  // Start camera (solo cuando el kiosko está activo; en reposo la cámara está apagada)
  useEffect(() => {
    if (phase !== "kiosk" || !kioskActive) return;
    let active = true;               // el efecto sigue montado (kiosko activo)
    let recovering = false;          // evita recuperaciones solapadas
    let watchdogId = null;
    let lastStartTs = 0;             // para dar gracia tras (re)iniciar
    let wakeLock = null;

    const CAMERA_ATTEMPTS = [
      { video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } } },
      { video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } } },
      { video: { facingMode: { ideal: "user" }, width: { ideal: 960 }, height: { ideal: 720 } } },
      { video: { facingMode: { ideal: "user" }, width: { ideal: 640, min: 320 }, height: { ideal: 480, min: 240 } } },
      { video: { facingMode: "user" } },
      { video: true },
    ];

    const stopStream = () => {
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        streamRef.current = null;
      }
    };

    // Escucha muerte/silencio del track (Safari mata el stream en sesiones largas).
    const attachTrackGuards = (stream) => {
      const track = stream.getVideoTracks()[0];
      if (!track) return;
      track.addEventListener("ended", () => { if (active) recover("track-ended"); });
      track.addEventListener("mute", () => {
        // Safari a veces auto-recupera; si sigue muteado tras un momento, recuperar.
        setTimeout(() => {
          if (active && track.muted) recover("track-muted");
        }, 1500);
      });
    };

    const openStream = async () => {
      for (const constraints of CAMERA_ATTEMPTS) {
        try {
          const s = await navigator.mediaDevices.getUserMedia(constraints);
          if (s) return s;
        } catch { /* siguiente combinación */ }
      }
      return null;
    };

    const attachStream = async (stream) => {
      streamRef.current = stream;
      attachTrackGuards(stream);
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        try { await video.play(); } catch { /* autoplay lo maneja el atributo */ }
        setCameraReady(true);
      }
      lastStartTs = Date.now();
    };

    // Wake Lock: reduce que iOS considere la página inactiva (best-effort).
    const acquireWakeLock = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener?.("release", () => { wakeLock = null; });
        }
      } catch { /* no soportado / denegado → ignorar */ }
    };
    const releaseWakeLock = async () => {
      try { await wakeLock?.release?.(); } catch { /* noop */ }
      wakeLock = null;
    };

    // Recuperación automática con backoff.
    const recover = async (reason) => {
      if (!active || recovering) return;
      recovering = true;
      setCameraReady(false);
      const delays = [800, 1500, 3000, 5000, 8000, 10000];
      let i = 0;
      // eslint-disable-next-line no-constant-condition
      while (active) {
        stopStream();
        const s = await openStream();
        if (s && active) {
          await attachStream(s);
          break;
        }
        const wait = delays[Math.min(i, delays.length - 1)];
        i += 1;
        await new Promise((r) => setTimeout(r, wait));
      }
      recovering = false;
    };

    const isStreamDead = () => {
      const stream = streamRef.current;
      const video = videoRef.current;
      if (!stream || !video) return true;
      const track = stream.getVideoTracks()[0];
      if (!track) return true;
      if (track.readyState !== "live") return true;   // track terminado
      if (track.muted) return true;                    // silenciado por el SO
      if (video.readyState < 2) return true;           // sin datos de video
      return false;
    };

    // Watchdog: Safari a veces mata el stream SIN disparar eventos.
    const startWatchdog = () => {
      watchdogId = setInterval(() => {
        if (!active || recovering) return;
        if (Date.now() - lastStartTs < 5000) return;   // gracia tras (re)iniciar
        if (isStreamDead()) recover("watchdog");
      }, 12000);
    };

    // Al volver el foco/visibilidad, reanudar y verificar el stream.
    const onVisible = async () => {
      if (!active) return;
      if (document.visibilityState !== "visible") return;
      acquireWakeLock();
      const video = videoRef.current;
      if (isStreamDead()) {
        recover("visibility");
      } else if (video) {
        try { await video.play(); } catch { /* noop */ }
      }
    };

    const startCamera = async () => {
      const stream = await openStream();
      if (!stream) {
        toast.error("No se pudo acceder a la camara");
        return;
      }
      if (!active) { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } return; }
      await attachStream(stream);
      acquireWakeLock();
      startWatchdog();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    startCamera();

    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      if (watchdogId) clearInterval(watchdogId);
      releaseWakeLock();
      stopStream();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCameraReady(false);
    };
  }, [phase, kioskActive]);

  // Draw face mesh on canvas
  const drawScanSweep = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const displayW = canvas.clientWidth;
    const displayH = canvas.clientHeight;
    if (!displayW || !displayH) return;
    const dpr = window.devicePixelRatio || 1;
    const bufW = Math.round(displayW * dpr);
    const bufH = Math.round(displayH * dpr);
    if (canvas.width !== bufW || canvas.height !== bufH) {
      canvas.width = bufW;
      canvas.height = bufH;
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, displayW, displayH);

    // Barrido tipo Face ID acotado al rectángulo de las 4 esquinas guía.
    const guide = getGuideRect(displayW, displayH);
    const gw = guide.right - guide.left;
    const gh = guide.bottom - guide.top;

    const CYCLE_MS = 3800;
    const phase01 = (Date.now() % CYCLE_MS) / CYCLE_MS;
    const bounce = phase01 < 0.5 ? phase01 * 2 : (1 - phase01) * 2;
    const scanY = guide.top + bounce * gh;

    const SIDE_INSET = gw * 0.06;
    const scanLeft = guide.left + SIDE_INSET;
    const scanRight = guide.right - SIDE_INSET;
    const scanW = scanRight - scanLeft;

    const HALO = 14;
    const grad = ctx.createLinearGradient(0, scanY - HALO, 0, scanY + HALO);
    grad.addColorStop(0.0,  "rgba(255, 255, 255, 0)");
    grad.addColorStop(0.45, "rgba(255, 255, 255, 0.08)");
    grad.addColorStop(0.5,  "rgba(255, 255, 255, 0.30)");
    grad.addColorStop(0.55, "rgba(255, 255, 255, 0.08)");
    grad.addColorStop(1.0,  "rgba(255, 255, 255, 0)");
    ctx.fillStyle = grad;
    ctx.fillRect(scanLeft, scanY - HALO, scanW, HALO * 2);

    ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(scanLeft, scanY);
    ctx.lineTo(scanRight, scanY);
    ctx.stroke();
  }, []);

  // Clear canvas
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFaceBox(null);
  }, []);

  // Guide rect (matches the 4 white corner markers in UI)
  // Mantener en sync con el div .style en el render (88% alto · aspect 1/1.15)
  const getGuideRect = (w, h) => {
    const gh = h * 0.88;
    const gw = gh / 1.15;
    return {
      left: (w - gw) / 2,
      top: (h - gh) / 2,
      right: (w + gw) / 2,
      bottom: (h + gh) / 2,
    };
  };

  // Unified detection loop: fast landmark tracking for mesh + throttled descriptor matching
  useEffect(() => {
    if (!cameraReady || !modelsLoaded || phase !== "kiosk" || !kioskActive) return;

    let running = true;
    let busy = false;
    let lastMatchAttempt = 0;
    let lastFaceSeen = performance.now();
    let detectCanvas = null;        // canvas 2D para copiar el frame del video
    const MATCH_INTERVAL_MS = 700;
    const FACE_LOST_MS = 1500;

    const loop = async () => {
      if (!running) return;

      const video = videoRef.current;
      // Skip work if busy, feedback showing, or video not ready
      if (
        busy ||
        feedbackRef.current ||
        !video ||
        video.readyState < 2 ||
        video.videoWidth === 0
      ) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      busy = true;
      try {
        const now = performance.now();
        const enrolled = enrolledRef.current;
        // Always keep matching (to refresh confidence dynamically)
        const shouldMatch =
          enrolled.length > 0 &&
          now - lastMatchAttempt >= MATCH_INTERVAL_MS;

        const vw = video.videoWidth || 480;
        const vh = video.videoHeight || 360;

        // Copiar el frame del <video> a un canvas 2D y detectar sobre el canvas.
        // Motivo: en algunas tablets Android (p.ej. Honor Pad X9a) el video se
        // muestra correctamente pero tfjs/WebGL NO puede leer los píxeles del
        // elemento <video> en esa GPU → detectSingleFace(video) devuelve null en
        // cada frame y el kiosko "nunca detecta". Detectar sobre un canvas 2D
        // (que sí recibe el frame vía drawImage) resuelve el caso y es inofensivo
        // para los dispositivos que ya funcionaban.
        let input = video;
        try {
          if (!detectCanvas) detectCanvas = document.createElement("canvas");
          if (detectCanvas.width !== vw) detectCanvas.width = vw;
          if (detectCanvas.height !== vh) detectCanvas.height = vh;
          const dctx = detectCanvas.getContext("2d");
          dctx.drawImage(video, 0, 0, vw, vh);
          input = detectCanvas;
        } catch {
          input = video;
        }

        let detection;
        if (shouldMatch) {
          lastMatchAttempt = now;
          setDetecting(true);
          detection = await faceapi
            .detectSingleFace(
              input,
              // Alcance máximo: inputSize 416 permite reconocer caras más
              // pequeñas/lejanas (~50-60 cm). scoreThreshold 0.4 acepta rostros
              // de menor tamaño en cuadro.
              new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.4, inputSize: 416 })
            )
            .withFaceLandmarks(true)
            .withFaceDescriptor();
          setDetecting(false);
        } else {
          // Fast path: solo landmarks para el seguimiento de la malla.
          // inputSize 320 mantiene el rastreo de caras lejanas fluido.
          detection = await faceapi
            .detectSingleFace(
              input,
              new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.4, inputSize: 320 })
            )
            .withFaceLandmarks(true);
        }

        if (detection) {
          lastFaceSeen = now;
          lastActivityRef.current = Date.now(); // hay rostro → no entrar en reposo

          // Compute face bbox in display coords to check if it's inside the 4-corner guide.
          const canvas = canvasRef.current;
          const dw = canvas?.clientWidth || vw;
          const dh = canvas?.clientHeight || vh;
          // El video se muestra con object-fit: cover (escala uniforme + recorte
          // centrado). Mapear los landmarks con la MISMA transformación (escala =
          // max, offset centrado, espejo horizontal) para que el marco-guía y la
          // identificación coincidan con lo que ve el trabajador (antes usaba una
          // escala no uniforme dw/vw, dh/vh → rostro "deformado"/fuera de marco).
          const cover = Math.max(dw / vw, dh / vh);
          const offX = (dw - vw * cover) / 2;
          const offY = (dh - vh * cover) / 2;
          let minX = Infinity, maxX = 0, minY = Infinity, maxY = 0;
          for (const p of detection.landmarks.positions) {
            const xx = dw - (p.x * cover + offX);
            const yy = p.y * cover + offY;
            if (xx < minX) minX = xx;
            if (xx > maxX) maxX = xx;
            if (yy < minY) minY = yy;
            if (yy > maxY) maxY = yy;
          }
          const guide = getGuideRect(dw, dh);
          // Allow tiny tolerance (2px) to avoid jitter
          const TOL = 2;
          const inFrame =
            minX >= guide.left - TOL &&
            maxX <= guide.right + TOL &&
            minY >= guide.top - TOL &&
            maxY <= guide.bottom + TOL;
          setFaceOutOfFrame(!inFrame);

          // Always re-run matching when we have a descriptor, but only if face is inside the frame
          if (detection.descriptor) {
            if (inFrame) {
              const descriptor = Array.from(detection.descriptor);
              let bestMatch = null;
              let bestDist = 1;
              for (const e of enrolled) {
                for (const ref of e.descriptors) {
                  const dist = faceapi.euclideanDistance(descriptor, ref);
                  if (dist < bestDist) {
                    bestDist = dist;
                    bestMatch = e;
                  }
                }
              }
              if (bestMatch && bestDist < 0.55) {
                const confidence = Math.round((1 - bestDist) * 100);
                const current = identifiedRef.current;
                if (current && current.employee_id === bestMatch.employee_id) {
                  if (Math.abs(current.confidence - confidence) >= 1) {
                    setIdentifiedEmployee({ ...current, confidence });
                  }
                } else {
                  setIdentifiedEmployee({ ...bestMatch, confidence });
                }
                failedMatchCountRef.current = 0;
                setShowPinFallback(false);
              } else {
                if (identifiedRef.current) setIdentifiedEmployee(null);
                failedMatchCountRef.current += 1;
                if (failedMatchCountRef.current >= 4) {
                  setShowPinFallback(true);
                }
              }
            } else {
              // Face outside guide → can't trust match, clear identification
              if (identifiedRef.current) setIdentifiedEmployee(null);
            }
          }

          // Ubicar el label del nombre a la derecha del rostro (coords display)
          setFaceBox({
            nameX: (Math.min(maxX + 16, dw) / dw) * 100,
            nameY: (((minY + maxY) / 2) / dh) * 100,
          });
        } else {
          setFaceBox(null);
          setFaceOutOfFrame(false);
          // Face lost for a while → reset identification
          if (identifiedRef.current && now - lastFaceSeen > FACE_LOST_MS) {
            setIdentifiedEmployee(null);
          }
        }
      } catch {
        /* ignore detection errors */
      }
      busy = false;
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cameraReady, modelsLoaded, phase, kioskActive]);

  // ── Barrido de escaneo continuo y suave (independiente de la detección) ──
  // Antes la línea de escaneo se dibujaba solo cuando había detección (loop
  // asíncrono lento) → parpadeaba/"sin señal". Ahora corre a 60fps mientras el
  // kiosko escanea y se limpia cuando hay rostro identificado o feedback.
  useEffect(() => {
    if (phase !== "kiosk" || !kioskActive) return;
    let raf;
    let running = true;
    const tick = () => {
      if (!running) return;
      if (feedbackRef.current || identifiedRef.current) {
        const c = canvasRef.current;
        if (c) {
          const ctx = c.getContext("2d");
          if (ctx) ctx.clearRect(0, 0, c.width, c.height);
        }
      } else {
        drawScanSweep();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [phase, kioskActive, drawScanSweep]);

  // ── Heartbeat al backend cada 60s + auto-reload por inactividad ──────
  useEffect(() => {
    if (phase !== "kiosk") return;
    const deviceId = localStorage.getItem(DEVICE_ID_KEY);
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    if (!deviceId || !deviceToken) return;

    let cancelled = false;
    let lastActivity = Date.now();

    const onActivity = () => { lastActivity = Date.now(); };
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("touchstart", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);

    const beat = async () => {
      if (cancelled) return;
      try {
        const pending = queueGet().length;
        await api.post("/kiosk/heartbeat", {
          device_id: JSON.parse(deviceId),
          device_token: JSON.parse(deviceToken),
          pending_marks: pending,
        });
      } catch (err) {
        if (err.response?.status === 401) {
          // Sesión revocada por admin → reset
          toast.error("Sesión cerrada por el administrador. Reactive con un código nuevo.");
          resetSetup();
          return;
        }
        // Errores de red → silenciosos, reintentará en el siguiente intervalo
      }
      // Si está mucho tiempo sin actividad y la cámara puede haberse colgado, reload
      if (Date.now() - lastActivity > IDLE_RELOAD_AFTER_MS && document.visibilityState === "hidden") {
        window.location.reload();
      }
    };

    // Primer beat inmediato y luego cada 60s
    beat();
    const tid = setInterval(beat, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(tid);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("keydown", onActivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Reposo por inactividad: vuelve a la pantalla de reposo (cámara apagada) ──
  useEffect(() => {
    if (phase !== "kiosk" || !kioskActive) return;
    const tid = setInterval(() => {
      if (Date.now() - lastActivityRef.current > KIOSK_IDLE_MS) {
        setKioskActive(false);
      }
    }, 4000);
    return () => clearInterval(tid);
  }, [phase, kioskActive]);

  // ── Señal de refresco: si cambian los rostros enrolados, refresca en 2º plano ──
  useEffect(() => {
    if (phase !== "kiosk") return;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/kiosk/faces-version");
        if (cancelled) return;
        const prev = facesVersionRef.current;
        if (prev && (prev.count !== data.count || prev.latest !== data.latest)) {
          refreshEnrolledFaces();
        }
        facesVersionRef.current = data;
      } catch { /* red intermitente → reintenta en el próximo ciclo */ }
    };
    check();
    const tid = setInterval(check, FACES_SIGNAL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(tid); };
  }, [phase, refreshEnrolledFaces]);

  const handleMark = async (employeeId, method, employeeMeta) => {
    lastActivityRef.current = Date.now(); // marcar = actividad
    const evtLabel = EVENT_TYPES.find((e) => e.value === selectedEvent)?.label || selectedEvent;

    // ── Validación local PREVIA (protege también offline) ─────────────
    // Si tenemos un status calculado y el evento no está permitido, rechazamos
    // antes de generar la marca. OFFLINE: NO se aplica este bloqueo — el estado
    // local no conoce permisos de reingreso del servidor, así que se permite la
    // marca y el servidor valida al sincronizar.
    const allowed = employeeStatus?.allowed_events;
    if (
      !employeeStatus?.offline_status &&
      Array.isArray(allowed) &&
      allowed.length > 0 &&
      !allowed.includes(selectedEvent)
    ) {
      const reason = employeeStatus?.has_check_out
        ? "Jornada ya finalizada. Si necesita reingresar, RRHH debe otorgar el permiso."
        : `«${evtLabel}» no es la siguiente marca esperada para este empleado.`;
      setFeedback({ type: "error", message: reason });
      setTimeout(() => {
        setFeedback(null);
        setIdentifiedEmployee(null);
        setFaceBox(null);
        clearCanvas();
        setShowPin(false);
        setPinCode("");
      }, 3000);
      return;
    }

    const payload = {
      employee_id: employeeId,
      branch_id: selectedBranch.id,
      event_type: selectedEvent,
      method,
      client_timestamp: new Date().toISOString(),
    };
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("__offline__");
      }
      const { data } = await api.post("/kiosk/mark", payload);
      // Registrar la marca en el tracker local para mantener consistencia
      dayStateRecord(employeeId, selectedEvent);
      setFeedback({
        type: "success",
        eventLabel: evtLabel,
        eventTime: data.mark?.event_time || new Date().toISOString(),
        employee: data.employee_name,
        code: data.employee_code,
        nationalId: identifiedEmployee?.national_id || data.national_id || employeeMeta?.national_id || "",
      });
      // Refresh recent marks (best-effort; ignore if offline)
      try {
        const marksRes = await api.get("/kiosk/recent-marks", {
          params: { branch_id: selectedBranch.id },
        });
        setRecentMarks(marksRes.data);
        try {
          localStorage.setItem(`${RECENT_CACHE_KEY}_${selectedBranch.id}`, JSON.stringify(marksRes.data));
        } catch { /* quota */ }
      } catch { /* keep previous marks */ }
    } catch (err) {
      if (isNetworkError(err)) {
        // Queue the mark for later flush
        const queued = queueGet();
        queued.push(payload);
        queueSet(queued);
        setPendingCount(queued.length);
        // Registrar en el tracker local para que las próximas marcas
        // del mismo empleado calculen correctamente el siguiente evento permitido.
        dayStateRecord(employeeId, selectedEvent);
        // Optimistic local mark so kiosk still feels live
        const optimistic = {
          id: `pending-${payload.client_timestamp}`,
          employee_id: employeeId,
          employee_name: employeeMeta?.full_name || identifiedEmployee?.full_name || "Empleado",
          employee_code: employeeMeta?.employee_code || identifiedEmployee?.employee_code || "",
          event_type: selectedEvent,
          event_time: payload.client_timestamp,
          method,
          pending_sync: true,
        };
        setRecentMarks((prev) => [optimistic, ...prev].slice(0, 20));
        setFeedback({
          type: "success",
          eventLabel: evtLabel,
          eventTime: payload.client_timestamp,
          employee: optimistic.employee_name,
          code: optimistic.employee_code,
          nationalId: identifiedEmployee?.national_id || employeeMeta?.national_id || "",
          offline: true,
        });
      } else {
        const msg = err?.response?.data?.detail || "Error al registrar marca";
        setFeedback({ type: "error", message: msg });
      }
    }
    // Reset after 3 seconds
    setTimeout(() => {
      setFeedback(null);
      setIdentifiedEmployee(null);
      setFaceBox(null);
      clearCanvas();
      setShowPin(false);
      setPinCode("");
      setShowPinFallback(false);
      failedMatchCountRef.current = 0;
    }, 3000);
  };

  const handlePinSubmit = async () => {
    if (pinCode.length < 4) return;
    try {
      const { data } = await api.post("/kiosk/verify-pin", {
        pin_code: pinCode,
        branch_id: selectedBranch.id,
      });
      await handleMark(data.id, "pin", {
        full_name: data.full_name,
        employee_code: data.employee_code,
        national_id: data.national_id,
      });
    } catch {
      setFeedback({ type: "error", message: "PIN invalido" });
      setTimeout(() => {
        setFeedback(null);
        setPinCode("");
      }, 2000);
    }
  };

  // Activate device with code
  const handleActivate = async () => {
    if (!activationCode.trim()) return;
    setActivating(true);
    try {
      // Detect device info from browser
      const ua = navigator.userAgent;
      let deviceLabel = "Dispositivo";
      if (/Android/.test(ua)) {
        const match = ua.match(/;\s*([^;)]+)\s*Build/);
        deviceLabel = match ? match[1].trim() : "Android";
      } else if (/iPad/.test(ua)) {
        deviceLabel = "iPad";
      } else if (/iPhone/.test(ua)) {
        deviceLabel = "iPhone";
      } else if (/Windows/.test(ua)) {
        deviceLabel = "PC Windows";
      } else if (/Mac/.test(ua)) {
        deviceLabel = "Mac";
      }
      const platform = navigator.platform || "";

      const { data } = await api.post("/kiosk/activate", {
        activation_code: activationCode,
        device_label: deviceLabel,
        platform: platform,
        user_agent: ua,
      });
      localStorage.setItem(DEVICE_ID_KEY, JSON.stringify(data.device_id));
      localStorage.setItem(DEVICE_TOKEN_KEY, JSON.stringify(data.device_token));
      const branch = { id: data.branch_id, name: data.branch_name, code: data.branch_code };
      localStorage.setItem(BRANCH_KEY, JSON.stringify(branch));
      setDeviceInfo(data);
      setSelectedBranch(branch);
      setPhase("loading");
    } catch (err) {
      const msg = err.response?.data?.detail || "Codigo invalido";
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  };

  const resetSetup = () => {
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    localStorage.removeItem(BRANCH_KEY);
    setDeviceInfo(null);
    setSelectedBranch(null);
    setActivationCode("");
    setPhase("setup");
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const KIOSK_TZ = "America/Costa_Rica";
  const formatTime = (d) => d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: KIOSK_TZ });
  const formatDate = (d) => d.toLocaleDateString("es-CR", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: KIOSK_TZ });
  const formatEventTime = (iso) => { try { return new Date(iso).toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: KIOSK_TZ }); } catch { return ""; } };
  // Big-clock helpers (HH:MM only, plus weekday + dd/mm/yyyy)
  const clockHHMM = (d) => d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: KIOSK_TZ });
  const clockDateLong = (d) => {
    const weekday = d.toLocaleDateString("es-CR", { weekday: "long", timeZone: KIOSK_TZ });
    const ddmmyyyy = d.toLocaleDateString("es-CR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: KIOSK_TZ });
    return `${weekday}, ${ddmmyyyy}`;
  };
  const eventLabel = (t) => EVENT_TYPES.find((e) => e.value === t)?.label || t;

  // ─── SETUP SCREEN ───
  if (phase === "setup") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="kiosk-setup">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-5">
              <img src="/dadao-logo-white.png" alt="DADAO · VETCOMER WORKFORCE SYSTEM" className="h-9 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              {selectedBranch?.name || "Activación de kiosko"}
            </h1>
            <p className="text-zinc-500 text-sm mt-1">
              {selectedBranch?.name ? "Activar dispositivo" : "Ingrese el código provisto por RRHH"}
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
            <p className="text-sm text-zinc-400 mb-4 text-center">
              Ingrese el codigo de activacion proporcionado por el administrador
            </p>
            <Input
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleActivate()}
              placeholder="VET-XXXX-XX"
              className="h-14 text-center text-xl tracking-[0.2em] font-mono bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 mb-4"
              autoFocus
              data-testid="kiosk-activation-code-input"
            />
            <Button
              className="w-full h-12 bg-white text-zinc-950 hover:bg-zinc-200 font-bold text-sm rounded-xl"
              onClick={handleActivate}
              disabled={activating || !activationCode.trim()}
              data-testid="kiosk-activate-button"
            >
              {activating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activando...</>
              ) : (
                "Activar dispositivo"
              )}
            </Button>
          </div>

          <p className="text-center text-zinc-700 text-xs mt-6">
            Solicite un nuevo código al administrador o departamento de RRHH.
          </p>
        </div>
      </div>
    );
  }

  // ─── LOADING SCREEN ───
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="kiosk-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-white animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">Cargando modelos biometricos...</p>
        </div>
      </div>
    );
  }

  // ─── MAIN KIOSK SCREEN ───
  // Reorder buttons for 2x2 grid:
  //   [ Entrada     ] [ Sal. Comida ]
  //   [ Salida      ] [ Reg. Comida ]
  const buttonOrder = ["check_in", "meal_out", "check_out", "meal_in"];
  const buttonsForGrid = buttonOrder
    .map((v) => EVENT_TYPES.find((e) => e.value === v))
    .filter(Boolean);

  // After check_out, the day is finalized — no more marks allowed in this kiosk.
  // EXCEPT: si RRHH otorgó permiso de reingreso, o si ya se inició un segundo bloque
  // (split-shift), el trabajador puede seguir marcando.
  // OFFLINE: nunca se bloquea. El estado local no puede conocer permisos de
  // reingreso otorgados en el servidor, así que en modo offline SIEMPRE se permite
  // marcar y el servidor valida al sincronizar (marcas encoladas).
  const dayFinalized =
    !!identifiedEmployee &&
    !!employeeStatus &&
    !employeeStatus.offline_status &&
    employeeStatus.has_check_out === true &&
    !employeeStatus.reentry_allowed &&
    !employeeStatus.is_split_shift_active;

  // ── Pantalla de reposo: solo hora grande + mensaje; toca para marcar ──
  if (phase === "kiosk" && !kioskActive) {
    return (
      <div
        onClick={wakeKiosk}
        className="min-h-screen w-screen relative overflow-hidden bg-zinc-950 text-white select-none cursor-pointer flex flex-col items-center justify-center gap-8"
        data-testid="kiosk-standby"
        role="button"
        aria-label="Toca para marcar"
      >
        {selectedBranch?.name && (
          <p
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-100 text-center px-6"
            data-testid="kiosk-standby-branch"
          >
            {selectedBranch.name}
          </p>
        )}
        <p className="text-8xl sm:text-9xl font-semibold tracking-tight tabular-nums leading-none" data-testid="kiosk-standby-clock">
          {formatTime(clock)}
        </p>
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 animate-pulse">
          Toca la pantalla para registrar tu marca
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen bg-zinc-950 text-white select-none overflow-x-hidden" data-testid="kiosk-page">

      {/* Compact header */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-2 border-b border-zinc-900">
        <div className="flex items-center gap-3">
          <img src="/dadao-logo-white.png" alt="DADAO" className="h-5 w-auto opacity-90" />
          {(!isOnline || pendingCount > 0) && (
            <div
              className="ml-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500"
              data-testid="kiosk-offline-badge"
              title={!isOnline ? "Sin conexion — las marcas se guardan localmente" : `${pendingCount} marca(s) por sincronizar`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${!isOnline ? "bg-red-500 animate-pulse" : "bg-amber-500"}`} />
              {!isOnline ? "Sin conexión" : `Sincronizando ${pendingCount}`}
            </div>
          )}
        </div>
        <div className="text-right" data-testid="kiosk-branch-name">
          <p className="text-sm font-medium text-zinc-200 tracking-tight leading-tight">{selectedBranch?.name}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 leading-tight">{selectedBranch?.code}</p>
        </div>
      </div>

      {/* Responsive layout: stacked on portrait, two columns on landscape */}
      <div className="grid grid-cols-1 landscape:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 landscape:h-[calc(100vh-49px)]">

        {/* ─── LEFT COLUMN: ONLY CAMERA ─── */}
        <div className="relative rounded-xl overflow-hidden border border-zinc-900 bg-black landscape:h-full aspect-[3/4] landscape:aspect-auto">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
            onLoadedData={() => setCameraReady(true)}
          />
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

          {/* 4 reference corners — usan casi todo el alto disponible */}
          {!feedback && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="relative" style={{ height: "88%", aspectRatio: "1 / 1.15" }}>
                <div className={`absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 rounded-tl-[10px] transition-colors ${faceOutOfFrame ? "border-amber-400/80" : "border-white/55"}`} />
                <div className={`absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 rounded-tr-[10px] transition-colors ${faceOutOfFrame ? "border-amber-400/80" : "border-white/55"}`} />
                <div className={`absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 rounded-bl-[10px] transition-colors ${faceOutOfFrame ? "border-amber-400/80" : "border-white/55"}`} />
                <div className={`absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 rounded-br-[10px] transition-colors ${faceOutOfFrame ? "border-amber-400/80" : "border-white/55"}`} />
              </div>
            </div>
          )}

          {/* Out-of-frame */}
          {!feedback && faceOutOfFrame && !identifiedEmployee && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[15] pointer-events-none">
              <div className="flex items-center gap-2 bg-zinc-950/85 backdrop-blur-sm px-3 py-1.5 rounded-full border border-zinc-800">
                <ScanFace className="w-3.5 h-3.5 text-zinc-400" />
                <span className="text-[11px] font-medium text-zinc-300">Ubique su rostro dentro del marco</span>
              </div>
            </div>
          )}

          {/* Status badge - solo indicador de "Detectando" (sin contador de rostros) */}
          {!feedback && !identifiedEmployee && detecting && (
            <div className="absolute top-3 left-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-400">
                <Loader2 className="w-3 h-3 animate-spin" /> Detectando
              </div>
            </div>
          )}

          {/* Feedback overlay */}
          {feedback && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-zinc-950/95 px-6">
              {feedback.type === "success" ? (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-400">
                      {feedback.eventLabel} {feedback.offline ? "· Sin conexión" : "registrada"}
                    </span>
                  </div>
                  <p className="text-7xl sm:text-8xl font-light tabular-nums tracking-tight text-white leading-none" data-testid="feedback-time">
                    {formatEventTime(feedback.eventTime)}
                  </p>
                  <div className="mt-6 text-center">
                    <p className="text-lg sm:text-xl font-semibold text-zinc-100 leading-tight" data-testid="feedback-employee">
                      {feedback.employee}
                    </p>
                    <div className="flex items-center justify-center gap-3 mt-1">
                      {feedback.code && (
                        <p className="text-[11px] font-mono text-zinc-500" data-testid="feedback-code">{feedback.code}</p>
                      )}
                      {feedback.nationalId && (
                        <>
                          <span className="text-zinc-700">·</span>
                          <p className="text-[11px] font-mono text-zinc-400" data-testid="feedback-cedula">Céd. {feedback.nationalId}</p>
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-zinc-300 mb-3" strokeWidth={1.4} />
                  <p className="text-center font-semibold leading-tight text-zinc-300 text-base max-w-xs">{feedback.message}</p>
                  {feedback.employee && (<p className="text-zinc-500 mt-2 text-xs">{feedback.employee} · {feedback.code}</p>)}
                </>
              )}
            </div>
          )}
        </div>

        {/* ─── RIGHT COLUMN: clock + buttons + confirm ─── */}
        <div className="flex flex-col landscape:h-full min-h-0">
          {/* Clock + Date */}
          <div className="text-center pt-2 pb-4 landscape:pb-6">
            <p className="text-6xl sm:text-7xl landscape:text-[11rem] leading-none font-light tabular-nums tracking-tight text-zinc-100" data-testid="kiosk-clock">
              {clockHHMM(clock)}
            </p>
            <p className="text-sm landscape:text-base text-zinc-500 capitalize mt-2" data-testid="kiosk-date">
              {clockDateLong(clock)}
            </p>
          </div>

          {/* Day-finalized banner */}
          {dayFinalized && (
            <div className="mx-auto w-full max-w-md mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-center" data-testid="kiosk-day-finalized">
              <p className="text-sm font-semibold text-amber-300 mb-0.5">Jornada finalizada hoy</p>
              <p className="text-xs text-zinc-400 leading-snug">
                Para registrar marcas adicionales debe acudir al departamento de Recursos Humanos.
              </p>
            </div>
          )}

          {/* 2x2 button grid */}
          {!feedback && (
            <div className="grid grid-cols-2 gap-3 mx-auto w-full max-w-md mb-4">
              {buttonsForGrid.map((evt) => {
                const Icon = evt.icon;
                const active = selectedEvent === evt.value;
                // OFFLINE: habilitar todos los botones (el servidor valida al
                // sincronizar). ONLINE: respetar los eventos permitidos por el
                // estado del servidor.
                const allowed = identifiedEmployee
                  ? (employeeStatus?.offline_status
                      ? true
                      : (employeeStatus?.allowed_events || []).includes(evt.value))
                  : !dayFinalized;
                const disabled = !allowed;
                const isCheckout = evt.value === "check_out";
                let stateClass;
                if (disabled) {
                  stateClass = "bg-transparent border-zinc-900 text-zinc-700 cursor-not-allowed opacity-30";
                } else if (isCheckout) {
                  // "Salida" en rojo para diferenciarla de "Sal. Comida"
                  stateClass = active
                    ? "bg-red-600 text-white border-red-600 shadow-lg shadow-red-900/30"
                    : "bg-red-950/30 border-red-800/60 text-red-300 hover:border-red-600 hover:text-red-200 hover:bg-red-900/30";
                } else if (active) {
                  stateClass = "bg-white text-zinc-950 border-white";
                } else {
                  stateClass = "bg-transparent border-zinc-800 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100";
                }
                return (
                  <button
                    key={evt.value}
                    onClick={() => !disabled && setSelectedEvent(evt.value)}
                    disabled={disabled}
                    className={`py-5 rounded-xl border text-center transition-all duration-150 ${stateClass}`}
                    data-testid={`kiosk-event-${evt.value}`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-1" strokeWidth={1.6} />
                    <span className="text-sm font-medium tracking-wide">{evt.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Confirm button */}
          {!feedback && identifiedEmployee && !dayFinalized && (
            <Button
              className={`mx-auto w-full max-w-md h-14 rounded-xl font-semibold text-base tracking-tight ${
                selectedEvent === "check_out"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-white text-zinc-950 hover:bg-zinc-100"
              }`}
              onClick={() => handleMark(identifiedEmployee.employee_id, "face")}
              data-testid="kiosk-confirm-button"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" strokeWidth={2} />
              Confirmar marca · {identifiedEmployee.full_name}
            </Button>
          )}

          {/* PIN fallback */}
          {!feedback && !identifiedEmployee && showPinFallback && !showPin && (
            <div className="mx-auto w-full max-w-md rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 flex items-center justify-between gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <KeyRound className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-amber-300 leading-tight">No logramos reconocerle</p>
                  <p className="text-[10px] text-zinc-500 leading-tight">Puede ingresar con su PIN.</p>
                </div>
              </div>
              <Button
                onClick={() => setShowPin(true)}
                className="h-8 px-3 bg-amber-500 hover:bg-amber-600 text-amber-950 font-semibold text-[11px] rounded-md shrink-0"
                data-testid="kiosk-show-pin"
              >
                Usar PIN
              </Button>
            </div>
          )}

          {!feedback && showPin && (
            <div className="mx-auto w-full max-w-md p-3 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-400 mb-2 text-center">Ingrese su PIN de empleado</p>
              <div className="flex gap-2">
                <Input
                  type="password"
                  maxLength={6}
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handlePinSubmit()}
                  placeholder="****"
                  className="flex-1 h-10 text-center text-lg tracking-[0.5em] bg-zinc-900 border-zinc-800 text-white"
                  autoFocus
                  data-testid="kiosk-pin-input"
                />
                <Button
                  className="h-10 px-5 bg-white text-zinc-950 hover:bg-zinc-100 font-semibold rounded-md text-sm"
                  onClick={handlePinSubmit}
                  disabled={pinCode.length < 4}
                  data-testid="kiosk-pin-submit"
                >
                  Marcar
                </Button>
              </div>
              <button onClick={() => { setShowPin(false); setPinCode(""); }} className="w-full mt-1.5 text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors">
                Volver a cámara
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
